import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getGroupQuizData,
  getContentResponse,
  GroupQuizData,
  ProjectSection,
  ContentResponse
} from '../../Services/UserProjectService';
import { useContentVariation } from '../../hooks/useContentVariation';

interface ContentWithResponse {
  content: any;
  response: ContentResponse | null;
}

const SectionReview: React.FC = () => {
  const { projectId, groupId, sectionIndex } = useParams<{
    projectId: string;
    groupId: string;
    sectionIndex: string;
  }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [section, setSection] = useState<ProjectSection | null>(null);
  const [groupData, setGroupData] = useState<GroupQuizData | null>(null);
  const [contentResponses, setContentResponses] = useState<ContentWithResponse[]>([]);

  const { getVariationText } = useContentVariation(groupData?.project);

  useEffect(() => {
    const fetchData = async () => {
      if (!groupId || sectionIndex === undefined) return;

      try {
        setLoading(true);
        const data = await getGroupQuizData(groupId);
        setGroupData(data);

        const sections = data.group.sections || [];
        const idx = parseInt(sectionIndex, 10);
        const targetSection = sections[idx];

        if (!targetSection) {
          setError('Sección no encontrada');
          return;
        }

        setSection(targetSection);

        // Fetch responses for all answerable content types
        const answerableTypes = [
          'QUESTION_SIMPLE',
          'QUESTION_MULTIPLE',
          'IMAGE_COMMENT',
          'TEXT_SELECTABLE'
        ];

        const contents = (targetSection.contents || []).filter(
          (c: any) => answerableTypes.includes(c.contentType)
        );

        const responses = await Promise.all(
          contents.map(async (content: any) => {
            try {
              const response = await getContentResponse(content.id);
              return { content, response };
            } catch {
              return { content, response: null };
            }
          })
        );

        setContentResponses(responses);
      } catch (err) {
        console.error('Error loading section review:', err);
        setError('Error al cargar la revisión de la sección');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [groupId, sectionIndex]);

  const ensureString = (val: unknown): string => {
    if (typeof val === 'string') return val;
    if (val === null || val === undefined) return '';
    if (typeof val === 'object') {
      // Try common text fields
      const obj = val as any;
      if (typeof obj.singular === 'string') return obj.singular;
      if (typeof obj.plural === 'string') return obj.plural;
      if (typeof obj.original === 'string') return obj.original;
      if (typeof obj.text === 'string') return obj.text;
      if (typeof obj.content === 'string') return obj.content;
      // Last resort: find any string value
      for (const v of Object.values(obj)) {
        if (typeof v === 'string' && v.trim()) return v;
      }
    }
    return String(val);
  };

  const stripHtml = (html: unknown): string => {
    const str = ensureString(html);
    if (!str) return '';
    const doc = new DOMParser().parseFromString(str, 'text/html');
    return doc.body.textContent || '';
  };

  const getQuestionText = (content: any): string => {
    let raw = '';

    // Try to get text from content variations
    if (content.content) {
      const text = getVariationText(content.content);
      if (text && typeof text === 'string') raw = text;
      else if (text && typeof text === 'object') raw = ensureString(text);
    }

    // Fallback: try questionText
    if (!raw && content.content?.questionText) {
      raw = ensureString(content.content.questionText);
    }
    if (!raw && content.title) raw = ensureString(content.title);
    if (!raw && content.name) raw = ensureString(content.name);

    return raw ? stripHtml(raw) : 'Pregunta';
  };

  const renderResponseContent = (item: ContentWithResponse) => {
    const { content, response } = item;

    if (!response) {
      return <span style={{ color: '#999', fontStyle: 'italic' }}>Sin respuesta</span>;
    }

    const responseData = response.responseContent;
    const answers = content.content?.answers || [];

    // IMAGE_COMMENT: show all images with their comments
    if (content.contentType === 'IMAGE_COMMENT') {
      try {
        const parsed = JSON.parse(responseData);

        // Normalize to array (supports legacy single-image {imageUrl, comment})
        const imageItems: { imageUrl: string; comment: string }[] = Array.isArray(parsed)
          ? parsed
          : parsed.imageUrl
            ? [parsed]
            : [];

        if (imageItems.length === 0) {
          return <span>{stripHtml(responseData)}</span>;
        }

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {imageItems.map((item: any, idx: number) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem',
                  padding: '0.5rem 0',
                  borderBottom: idx < imageItems.length - 1 ? '1px solid #f0f0f0' : 'none',
                }}
              >
                {item.imageUrl && (
                  <img
                    src={item.imageUrl}
                    alt={`Imagen ${idx + 1}`}
                    style={{
                      width: '160px',
                      height: '160px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      flexShrink: 0,
                    }}
                  />
                )}
                <p style={{
                  margin: 0,
                  fontSize: '0.9rem',
                  lineHeight: '1.5',
                  color: item.comment ? '#333' : '#999',
                  fontStyle: item.comment ? 'normal' : 'italic',
                  paddingTop: '0.15rem',
                }}>
                  {item.comment ? stripHtml(item.comment) : 'Sin comentario'}
                </p>
              </div>
            ))}
          </div>
        );
      } catch {
        return <span>{stripHtml(responseData)}</span>;
      }
    }

    // Helper: recursively extract readable text from any value
    const toReadableText = (val: unknown): string => {
      if (val === null || val === undefined) return '';
      if (typeof val === 'boolean') return val ? 'Sí' : 'No';
      if (typeof val === 'string') return stripHtml(val);
      if (typeof val === 'number') return String(val);
      if (Array.isArray(val)) return val.map(toReadableText).filter(Boolean).join(', ');
      if (typeof val === 'object') {
        const obj = val as Record<string, unknown>;
        // Skip internal keys like allowMultiple
        const values = Object.entries(obj)
          .filter(([k]) => !['allowMultiple'].includes(k))
          .map(([, v]) => toReadableText(v))
          .filter(Boolean);
        return values.join(', ');
      }
      return String(val);
    };

    // Try to parse JSON responses
    try {
      const parsed = JSON.parse(responseData);

      // QUESTION_MULTIPLE with selectedAnswers (indices) — resolve to answer text
      if (parsed && typeof parsed === 'object' && 'selectedAnswers' in parsed) {
        const indices: number[] = parsed.selectedAnswers || [];
        const freeTexts: Record<string, string> = parsed.freeTextAnswers || {};

        const resolvedAnswers = indices.map((idx: number) => {
          const answerObj = answers[idx];
          const answerText = answerObj ? stripHtml(getVariationText(answerObj.text) || `Opción ${idx + 1}`) : `Opción ${idx + 1}`;
          const freeText = freeTexts[String(idx)]?.trim();
          return freeText ? `${answerText}: ${freeText}` : answerText;
        });

        if (resolvedAnswers.length === 1) {
          return <span>{resolvedAnswers[0]}</span>;
        }
        return (
          <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
            {resolvedAnswers.map((text, idx) => (
              <li key={idx}>{text}</li>
            ))}
          </ul>
        );
      }

      // Boolean response
      if (typeof parsed === 'boolean') {
        return <span>{parsed ? 'Sí' : 'No'}</span>;
      }

      // Any other object/array — extract all readable values
      if (parsed && typeof parsed === 'object') {
        const text = toReadableText(parsed);
        if (text) return <span>{text}</span>;
      }

      return <span>{stripHtml(String(parsed))}</span>;
    } catch {
      // Not JSON — plain text response (single choice text, free text, etc.)
      if (responseData === 'true') return <span>Sí</span>;
      if (responseData === 'false') return <span>No</span>;
      return <span>{stripHtml(responseData)}</span>;
    }
  };

  if (loading) {
    return (
      <div className="container py-5">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger" role="alert">
          <p>{error}</p>
          <button
            className="btn btn-outline-secondary mt-2"
            onClick={() => navigate('/user/dashboard')}
          >
            Volver al dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="hide-scrollbar"
      style={{
        height: '100vh',
        width: '100%',
        overflowY: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        paddingTop: '120px'
      }}
    >
      <div className="container-custom">
        {/* Back button + title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem 0' }}>
          <button
            onClick={() => navigate('/user/dashboard')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.25rem 0.5rem',
              color: 'var(--color-dark, #333)',
              display: 'flex',
              alignItems: 'center'
            }}
            title="Volver al dashboard"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
          </button>
          <h2 className="h4 mb-0">{section?.name || 'Revisión de sección'}</h2>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', paddingBottom: '3rem' }}>
            {contentResponses.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#666', padding: '2rem 0' }}>
                No hay preguntas con respuestas en esta sección.
              </div>
            ) : (
              contentResponses.map((item, index) => (
                <div
                  key={item.content.id || index}
                  style={{
                    padding: '1.25rem 0',
                    borderBottom: '1px solid #eee'
                  }}
                >
                  <div style={{ marginBottom: '0.75rem', fontWeight: 500 }}>
                    {getQuestionText(item.content)}
                  </div>
                  <div style={{ fontSize: '0.95rem' }}>
                    {renderResponseContent(item)}
                  </div>
                </div>
              ))
            )}
        </div>
      </div>
    </div>
  );
};

export default SectionReview;
